﻿module Illy.SafeStack.Client.MultiSelect

open Illy.SafeStack.Client.Shared
open Illy.SafeStack
open Fable.Core
open Fable.Core.JsInterop
open Elmish
open Fable.React
open Fable.React.Props
open System
open Elmish.React

let [<Literal>] MAX_DROPDOWN_HEIGHT = 300.
let [<Literal>] Z_INDEX_BOOSTER = 3000.

type 'a Model = 
    {
        Id: string
        Label: string
        ValidationError: string option
        ValidationWarning: string option
        Placeholder: string option
        IsMandatory: bool
        Options: ListType<'a>
        VisibleOptions: ListOption list
        SelectedOptions: ListOptionType<'a> list
        SelectedSnapshot: ListOptionType<'a> list
        Open: bool
        Summary: string
        SearchString: string
        ExtraCss: string
        DropdownPosition: HTMLElementPosition.PopupMetaData option
    }

let generateSummary placeholder selectedOptions = 
    if List.length selectedOptions = 0 then
        placeholder |> FSharp.Core.Option.defaultValue "Not Set"
    else
        selectedOptions 
        |> List.map ListOptionType.getListValue
        |> List.sort
        |> String.concat ", " 

let private convertOptions opts = ListOption.convertToList opts

let setExtraCss css model =
    { model with ExtraCss = css }

let private calculateVisibleOptions options snapshot (term: string) =
    let selectedOptions =
        snapshot
        |> List.map ListOptionType.getListOption

    let options = 
        convertOptions options

    let missing =
        selectedOptions
        |> List.except options

    match term.ToLowerInvariant() with 
    | "" -> options@missing
    | t -> options@missing |>  List.filter (fun x -> x.Value.ToLowerInvariant().Contains(t))

let init id label placeholder isMandatory options selectedOptions =
    let visible = calculateVisibleOptions options selectedOptions ""

    {
        Id = id 
        Label = label
        ValidationError = None
        ValidationWarning = None
        Placeholder = placeholder
        IsMandatory = isMandatory
        Options = options
        VisibleOptions = visible
        SelectedOptions = selectedOptions 
        
        //Snapshot of the selected options on init
        SelectedSnapshot = selectedOptions
        Open = false
        Summary = generateSummary placeholder selectedOptions
        SearchString = ""
        ExtraCss = ""
        DropdownPosition = None
    }

type Msg = 
    | ToggleOption of int
    | ToggleDropdown of isOpen: bool
    | SetSearchParams of string
    | AdjustDropdownPosition
    | SelectAll

let update<'a> (msg: Msg) (model: Model<'a>) : Model<'a>*Cmd<Msg> = 
    match msg with
    | ToggleDropdown isOpen -> 
        { model with Open = isOpen }, 
        if isOpen then Cmd.ofMsg AdjustDropdownPosition
        else []

    | SetSearchParams term -> 
        {
            model with 
                SearchString = term
                VisibleOptions = calculateVisibleOptions model.Options model.SelectedSnapshot term
        }, []
    | ToggleOption id ->
        let selected =
            match model.SelectedOptions |> List.tryFind (fun x -> 
                let opt = ListOptionType.getListOption x
                opt.Id = id) with
            | Some found ->
                [
                    for item in model.SelectedOptions do
                        let x = ListOptionType.getListOption item
                        if x.Id = (ListOptionType.getListOption found).Id then
                            ()
                        else
                            yield item
                ]
            | None ->
                let found = model.Options |> ListOption.getListItemById id
                found::model.SelectedOptions

        { model with 
            SelectedOptions = selected
            Summary = generateSummary model.Placeholder selected
            VisibleOptions = calculateVisibleOptions model.Options model.SelectedSnapshot model.SearchString
        }, []
    | AdjustDropdownPosition -> 
        let newPosition = HTMLElementPosition.calculatePopupPosition MAX_DROPDOWN_HEIGHT (HTMLElementPosition.HTMLElementId model.Id)
        {model with DropdownPosition = newPosition}, []
    | SelectAll -> 
        let allOptionsSelected  = 
            model.Options 
            |> ListOption.convertToList
            |> List.map (fun lo -> ListOption.getListItemById lo.Id model.Options)
        
        {
            model with 
                SelectedOptions = allOptionsSelected
                Summary = generateSummary model.Placeholder allOptionsSelected
                VisibleOptions = calculateVisibleOptions model.Options model.SelectedSnapshot model.SearchString
            }, []


let renderItem debounceToggleDropdown id groupIndex index name dispatch selectedOptions = 
    let formattedId = (sprintf "%s_%d_%d" name groupIndex index)
    div [
        ClassName "illy-checkboxlist-divider"
    ] [
        input [
            Id formattedId
            HTMLAttr.Type "checkbox"
            OnClick (fun _ -> debounceToggleDropdown true)
            OnChange (fun _ -> dispatch (ToggleOption id))
            Checked (
                match selectedOptions |> List.tryFind (fun x -> 
                    let opt = ListOptionType.getListOption x
                    opt.Id = id) with
                | Some _ -> true | None -> false
            )
        ]
        label [HtmlFor formattedId ] [ str name ]
    ]
   
let debounce timeoutMs dispatch =
    let mutable timeoutActive: int option = None 
    let mutable store = Unchecked.defaultof<Msg>

    fun (ev: Browser.Types.Event) ->
        store <- SetSearchParams ev.target?value
        if timeoutActive.IsSome then JS.clearTimeout timeoutActive.Value
    
        timeoutActive <- 
            JS.setTimeout (fun () ->
                dispatch store
                timeoutActive <- None) timeoutMs
            |> Some

let targetIsCheckboxlistSummary (target: Browser.Types.EventTarget) =
    let className: string = target?className
    className.Contains("illy-checkboxlist-summary-container")

let multiSelectInput loading (model: _ Model) (dispatch: Msg -> unit) =
    let debounceToggleDropdown = Shared.debounce 50 (fun (msg, value) -> dispatch (msg value)) ToggleDropdown
    
    let groups = model.VisibleOptions |> List.groupBy (fun x -> x.Group)
    let (ungrouped, grouped) = groups |> List.partition (fun x -> fst x = "")
    let boostedZ = 100. + (2.*Z_INDEX_BOOSTER)
    div [ 
            ClassName ("dropdowncheckboxlist illy-checkboxlist" + if model.ExtraCss = "" then "" else " " + model.ExtraCss)
            Id model.Id
            Rel model.Id
        ] [ 
            yield div [ 
                ClassName 
                    (jQueryUI.addSkeletonScreen
                        ("illy-checkboxlist-summary-container ui-helper-clearfix" + if model.Open then " expanded" else " collapsed")
                        None
                        loading
                    )
                OnClick (fun ev -> if not loading then debounceToggleDropdown (not model.Open))
                Style [ 
                    PaddingLeft "4px"
                ]
                TabIndex 0
            ] [
                div [
                    ClassName (jQueryUI.hideTextIfLoading "illy-checkboxlist-summary" None loading)
                    Style [
                        Float FloatOptions.Left  
                    ]
                    OnClick (fun ev -> ev.preventDefault())
                ] [ 
                    str (if model.SelectedOptions.Length = 0 then model.Placeholder |> FSharp.Core.Option.defaultValue "Not Set" else model.Summary)
                ]
                div [
                    ClassName ("handle ui-icon ui-icon-triangle-1-" + if model.Open then "n" else "s")
                    OnClick (fun ev -> ev.preventDefault())
                ] []
            ]
            if model.Open then 
                yield 
                    div [
                        let additionalClasses = 
                            model.DropdownPosition 
                            |> Option.map ((fun x -> x.Position) >> HTMLElementPosition.Position.toCSSClass)
                            |> Option.defaultValue ""

                        yield ClassName (sprintf "illy-checkboxlist-wrapper %s" additionalClasses)
                        yield Style [
                            yield ZIndex boostedZ
                            yield Clear ClearOptions.Both
                            
                            yield MaxHeight (sprintf "%fpx" MAX_DROPDOWN_HEIGHT)
                            yield! HTMLElementPosition.generatePositionStyleWithWidth model.DropdownPosition
                        ]

                        yield TabIndex -1
                        
                        yield OnFocus (fun _ -> debounceToggleDropdown true)
                        yield OnBlur 
                            (fun ev -> 
                                if ev.relatedTarget = null || targetIsCheckboxlistSummary ev.relatedTarget = false then
                                    debounceToggleDropdown false
                            )
                        yield OnScroll (fun _ -> debounceToggleDropdown true)
                    ]
                    [
                        input [
                            ClassName "illy-checkboxlist-searchbox"
                            HTMLAttr.Type "text"
                            OnChange (debounce 600 dispatch)
                            AutoFocus true
                            OnFocus (fun _ -> debounceToggleDropdown true)
                            Helpers.valueOrDefault model.SearchString
                        ]
                        div [
                            ClassName "illy-checkboxlist-wrapper-inner"
                            Style [
                                Position PositionOptions.Relative
                                ZIndex (boostedZ + 1.)
                            ]
                        ] [
                            for groupIndex , (g, opts) in List.indexed grouped do
                                yield div [ 
                                    ClassName "illy-checkboxlist-groupheader"
                                ] [ str g  ]
                                for index, option in List.indexed opts do
                                    yield renderItem debounceToggleDropdown (option.Id) (groupIndex+1) index option.Value dispatch model.SelectedOptions
                            if grouped.Length > 0 && ungrouped.Length > 0 then 
                                yield div [ 
                                    ClassName "illy-checkboxlist-groupheader"
                                ] [ str "Ungrouped"  ]
                            match ungrouped with
                            | [] -> ()
                            | [_,ung] ->
                                for index,option in List.indexed ung do
                                    yield renderItem debounceToggleDropdown (option.Id) 0 index option.Value dispatch model.SelectedOptions
                            | _ -> failwith "Unexpected duplicate ungrouped collection."
                        ]
                    ]
        ]

let multiSelect loading model dispatch=    
    jQueryUI.inputGroup [
        jQueryUI.labelWithMandatory model.Id model.Label model.ValidationError model.ValidationWarning loading model.IsMandatory
        multiSelectInput loading model dispatch  
    ]
    
let multiSelectWithAdditional loading displaySelectAll displayDeselectAll model dispatch =    
    jQueryUI.inputGroup [
        yield div [
            Style [
                Display DisplayOptions.Flex
                JustifyContent "space-between"
            ]            
        ] [
            yield jQueryUI.labelWithMandatory model.Id model.Label model.ValidationError model.ValidationWarning loading model.IsMandatory
            if displaySelectAll then yield a [ OnClick (fun _ -> SelectAll |> dispatch) ] [ str "Select All"]
        ]
        yield multiSelectInput loading model dispatch  
    ]

//utility functions
let resetValidations model = 
    { model with ValidationError = None; ValidationWarning = None }

let setValidations model errors warnings = 
    { model with ValidationError = errors; ValidationWarning = warnings }

let toListOptions (model: Model<_>) =
    model.SelectedOptions |> List.map ListOptionType.getListOption 

let resetSelectedOptions (model: Model<_>) = 
    {model with 
        SelectedOptions = [] 
        Summary = generateSummary model.Placeholder []
        VisibleOptions = calculateVisibleOptions model.Options model.SelectedSnapshot model.SearchString
    }